import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import introJs from 'intro.js';
import 'intro.js/introjs.css'; // Estilos base de la librería
import '../../styles/AppTour.css'; // Nuestros estilos personalizados IdevPlus

/**
 * AppTour: Un componente "invisible" que se monta encima de cualquier pantalla
 * y dibuja luces o focos sobre los elementos que le pasemos por la prop "steps".
 * Adaptado para usar raw Intro.js (Compatible con React 19)
 *
 * Props:
 *   skipUrl: URL a la que se navega cuando el usuario hace clic en "Saltar Tour".
 *            Por defecto navega a "/app" (Mis Aplicaciones).
 *            Pasar null para desactivar la navegación automática al saltar.
 */
const AppTour = ({ steps, runTour, setRunTour, onTourComplete, onTourExit, skipUrl = '/app', tourOptions = {} }) => {
    const navigate = useNavigate();
    // Referencia para saber si el tour terminó con "done" o con "skip"
    const completedRef = useRef(false);

    useEffect(() => {
        if (runTour) {
            completedRef.current = false;

            // Un micro retraso para garantizar que React pintó el DOM
            const timer = setTimeout(() => {
                const intro = introJs();
                intro.setOptions({
                    steps: steps,
                    doneLabel: 'Terminar',
                    nextLabel: 'Siguiente →',
                    prevLabel: '← Atrás',
                    skipLabel: 'Saltar tour',
                    hidePrev: false,
                    hideNext: false,
                    showProgress: true,
                    showBullets: false,
                    scrollToElement: true,
                    scrollPadding: 80,
                    smoothScroll: true,
                    tooltipClass: 'idevplus-tour-theme',
                    exitOnOverlayClick: false,
                    disableInteraction: false,
                    ...tourOptions
                });

                // Oculta el skip button de intro.js e inyecta uno propio en el header
                const injectSkipButton = () => {
                    const tooltip = document.querySelector('.idevplus-tour-theme.introjs-tooltip');
                    if (!tooltip) return;

                    // Ocultar el skip button original de intro.js
                    const originalSkip = tooltip.querySelector('.introjs-skipbutton');
                    if (originalSkip) {
                        originalSkip.style.setProperty('display', 'none', 'important');
                    }

                    // No crear dos veces el botón
                    if (tooltip.querySelector('.idevplus-skip-btn')) return;

                    const header = tooltip.querySelector('.introjs-tooltip-header');
                    if (!header) return;

                    const btn = document.createElement('button');
                    btn.className = 'idevplus-skip-btn';
                    btn.textContent = 'Saltar tour';
                    btn.style.cssText = `
                        background: rgba(255,255,255,0.15);
                        color: rgba(255,255,255,0.9);
                        border: 1px solid rgba(255,255,255,0.3);
                        border-radius: 100px;
                        padding: 4px 14px;
                        font-size: 12px;
                        font-weight: 600;
                        cursor: pointer;
                        white-space: nowrap;
                        line-height: 1.6;
                        margin-left: auto;
                        flex-shrink: 0;
                        transition: all 0.15s ease;
                        font-family: inherit;
                    `;
                    btn.onmouseenter = () => {
                        btn.style.background = 'rgba(255,255,255,0.28)';
                        btn.style.borderColor = 'rgba(255,255,255,0.55)';
                        btn.style.color = 'white';
                    };
                    btn.onmouseleave = () => {
                        btn.style.background = 'rgba(255,255,255,0.15)';
                        btn.style.borderColor = 'rgba(255,255,255,0.3)';
                        btn.style.color = 'rgba(255,255,255,0.9)';
                    };
                    btn.onclick = () => {
                        if (originalSkip) originalSkip.click();
                        else intro.exit(true);
                    };
                    header.appendChild(btn);
                };

                // oncomplete se dispara al hacer clic en el botón "Terminar/Done"

                intro.oncomplete(() => {
                    completedRef.current = true;
                    setRunTour(false);
                    if (onTourComplete) onTourComplete();
                });

                // onexit se dispara tanto al saltar como al completar.
                intro.onexit(() => {
                    setRunTour(false);
                    if (onTourExit) onTourExit();
                    if (!completedRef.current && skipUrl) {
                        navigate(skipUrl);
                    }
                });

                // Inyectar botón propio en cada cambio de paso
                intro.onchange(() => {
                    // Limpiar botón anterior al cambiar de paso
                    document.querySelectorAll('.idevplus-skip-btn').forEach(el => el.remove());
                    setTimeout(injectSkipButton, 120);
                });
                intro.onafterchange(() => { setTimeout(injectSkipButton, 120); });

                intro.start();
                setTimeout(injectSkipButton, 600);

            }, 300);

            return () => {
                clearTimeout(timer);
                // Si el componente se desmonta inesperadamente, ocultar cualquier tooltip fantasma
                const currentIntro = introJs();
                if (currentIntro) currentIntro.exit(true);
            };
        }
    }, [runTour, steps, setRunTour]);

    return null; // Este componente no renderiza HTML por sí mismo.
};

export default AppTour;
